import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { GET_MONTHLY_TAM_PRIME } from '@bloomays-lib/adapter.api-talent';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores';
import { errorLogger } from '../../helpers/error';
import MonthlyPrimeHeader from '../molecules/MonthlyPrimeHeader';
import MonthlyPrimeTable from '../molecules/MonthlyPrimeTable';
import { GetMonthlyTamPrimeQueryData, TamPrimeLine } from '../../types/monthlyPrime';
import { deriveHeaderAmounts, formatPrimeMonthLabel, getTamDisplayName } from '../../utils/monthlyPrime.utils';

const MonthlyPrime = () => {
  const { t } = useTranslation(['primes', 'random']);
  const auth = useAuthStore((state) => state.auth);
  const tamEmail = typeof auth?.user?.email === 'string' ? auth.user.email.trim() : '';
  const referenceDate = useMemo(() => new Date(), []);
  const monthLabel = formatPrimeMonthLabel(referenceDate);
  const tamDisplayName = getTamDisplayName(auth?.user);

  const { loading, error, data } = useQuery<GetMonthlyTamPrimeQueryData>(GET_MONTHLY_TAM_PRIME, {
    skip: !tamEmail,
    variables: {
      tamEmail,
      referenceDate,
    },
    context: { clientName: 'api.talents' },
    fetchPolicy: 'network-only',
  });

  if (!tamEmail) {
    return <ErrorMessage variant="body1">{t('missingTamEmail')}</ErrorMessage>;
  }

  if (error) {
    errorLogger(error, { extraInfos: 'getMonthlyTamPrime failed on /primes' });
    return <ErrorMessage variant="body1">{t('loadError')}</ErrorMessage>;
  }

  const aggregate = data?.getMonthlyTamPrime;
  const primes: TamPrimeLine[] = loading ? [] : aggregate?.primes ?? [];
  const amounts = !loading && aggregate ? deriveHeaderAmounts(aggregate) : undefined;

  return (
    <Container>
      <MonthlyPrimeHeader tamDisplayName={tamDisplayName} monthLabel={monthLabel} amounts={amounts} loading={loading} />
      <MonthlyPrimeTable rows={primes} loading={loading} />
    </Container>
  );
};

export default MonthlyPrime;

const Container = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  overflowX: 'hidden',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(3),
  color: theme.palette.error.main,
}));
